import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit, Trash, Clock, Users, DollarSign } from "lucide-react";
import type { ServiceOptionResponse } from "@/types/services";
import EditServiceOptionModal from "../modals/EditServiceOptionModal";
import ServiceOptionDetailDrawer from "./ServiceOptionDetailDrawer";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface ServiceOptionCardProps {
  serviceOption: ServiceOptionResponse;
}

export default function ServiceOptionCard({ serviceOption }: ServiceOptionCardProps) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return apiRequest('DELETE', `/marketplace/services/${serviceOption.service?.id}/options/${serviceOption.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serviceOptions", serviceOption.service?.id] });
      queryClient.invalidateQueries({ queryKey: ["services"] });
      toast({
        title: "Service option deleted",
        description: "The service option has been successfully deleted.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete the service option. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this service option?")) {
      deleteMutation.mutate();
    }
  };

  const formatPrice = (price?: number) => {
    if (!price) return "Free";
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: serviceOption.currency || 'AED'
    }).format(price);
  };

  const getPricingDisplay = () => {
    if (serviceOption.pricingModel === 'PerPerson') {
      return `${formatPrice(serviceOption.basePrice)}/person`;
    }
    return formatPrice(serviceOption.basePrice);
  };

  return (
    <>
      <div
        className="p-3 border rounded-lg space-y-2 cursor-pointer hover:bg-accent/5 transition-colors"
        onClick={() => {
          setShowDrawer(true);
        }}
      >
        <div className="flex justify-between items-start">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {serviceOption.pricingModel}
              </Badge>
              {serviceOption.targetAudience && (
                <Badge variant="secondary" className="text-xs">
                  {serviceOption.targetAudience}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              {serviceOption.duration && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {serviceOption.duration} min
                </span>
              )}
              {(serviceOption.minCapacity || serviceOption.maxCapacity) && (
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {serviceOption.minCapacity && serviceOption.maxCapacity
                    ? `${serviceOption.minCapacity}-${serviceOption.maxCapacity}`
                    : serviceOption.maxCapacity || serviceOption.minCapacity
                  } capacity
                </span>
              )}
              <span className="flex items-center gap-1">
                <DollarSign className="h-3 w-3" />
                {getPricingDisplay()}
              </span>
            </div>
          </div>
          <div className="flex gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={(e) => {
                e.stopPropagation();
                setShowEditModal(true);
              }}
            >
              <Edit className="h-3 w-3" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
            >
              <Trash className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {/* Additional details section */}
        {(serviceOption.discountType || serviceOption.claimLimit || serviceOption.notes) && (
          <div className="pt-2 border-t">
            <div className="text-xs text-muted-foreground">
              {serviceOption.discountType && serviceOption.discountValue && (
                <span className="inline-flex items-center gap-1 mr-2">
                  Discount: {serviceOption.discountType === 'Percentage'
                    ? `${serviceOption.discountValue}%`
                    : formatPrice(serviceOption.discountValue)}
                </span>
              )}
              {serviceOption.claimLimit && (
                <Badge variant="secondary" className="text-xs mr-1">
                  {serviceOption.claimLimit}
                </Badge>
              )}
              {serviceOption.claimFrequency && (
                <Badge variant="secondary" className="text-xs">
                  {serviceOption.claimFrequency}
                </Badge>
              )}
            </div>
            {serviceOption.notes && (
              <p className="text-xs text-muted-foreground mt-1 truncate">
                {serviceOption.notes}
              </p>
            )}
          </div>
        )}
      </div>

      <EditServiceOptionModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        serviceOption={serviceOption}
      />

      <ServiceOptionDetailDrawer
        serviceOption={serviceOption}
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
      />
    </>
  );
}